import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Building2,
  FileText,
  IndianRupee,
  RefreshCw,
  Activity,
  Trophy,
  Globe,
  CheckCircle,
  Clock,
  AlertTriangle,
  UserPlus
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformAnalytics = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/platform-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching platform analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load platform analytics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const averages = data?.averages || {};

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6 text-blue-600" />
            Platform-Wide Analytics
          </h1>
          <p className="text-gray-500">Aggregate statistics across all users (Super Admin Only)</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Platform Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-100">Total Users</p>
                <p className="text-3xl font-bold">{summary.total_users || 0}</p>
                <p className="text-xs text-blue-200">{summary.active_users || 0} active</p>
              </div>
              <Users className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-100">New This Month</p>
                <p className="text-3xl font-bold">{summary.new_users_this_month || 0}</p>
                <p className="text-xs text-green-200">user signups</p>
              </div>
              <UserPlus className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-100">Total Clients</p>
                <p className="text-3xl font-bold">{(summary.total_clients || 0).toLocaleString()}</p>
                <p className="text-xs text-purple-200">across platform</p>
              </div>
              <Building2 className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-100">Total Invoices</p>
                <p className="text-3xl font-bold">{(summary.total_invoices || 0).toLocaleString()}</p>
                <p className="text-xs text-orange-200">generated</p>
              </div>
              <FileText className="w-10 h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-100">Platform Revenue</p>
                <p className="text-2xl font-bold">₹{((summary.total_revenue || 0) / 100000).toFixed(1)}L</p>
                <p className="text-xs text-emerald-200">total invoiced</p>
              </div>
              <IndianRupee className="w-10 h-10 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-teal-100">Collected</p>
                <p className="text-2xl font-bold">₹{((summary.total_collected || 0) / 100000).toFixed(1)}L</p>
                <p className="text-xs text-teal-200">{averages.collection_rate || 0}% rate</p>
              </div>
              <TrendingUp className="w-10 h-10 text-teal-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Platform Averages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Platform Averages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{averages.avg_clients_per_user || 0}</p>
              <p className="text-sm text-gray-500">Clients per User</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">₹{((averages.avg_revenue_per_user || 0) / 1000).toFixed(1)}K</p>
              <p className="text-sm text-gray-500">Revenue per User</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">{averages.avg_tasks_per_user || 0}</p>
              <p className="text-sm text-gray-500">Tasks per User</p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{averages.task_completion_rate || 0}%</p>
              <p className="text-sm text-gray-500">Task Completion</p>
            </div>
            <div className="text-center p-4 bg-teal-50 rounded-lg">
              <p className="text-2xl font-bold text-teal-600">{averages.collection_rate || 0}%</p>
              <p className="text-sm text-gray-500">Collection Rate</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="growth" className="space-y-4">
        <TabsList>
          <TabsTrigger value="growth">
            <TrendingUp className="w-4 h-4 mr-2" />
            User Growth
          </TabsTrigger>
          <TabsTrigger value="revenue">
            <IndianRupee className="w-4 h-4 mr-2" />
            Platform Revenue
          </TabsTrigger>
          <TabsTrigger value="top-users">
            <Trophy className="w-4 h-4 mr-2" />
            Top Performers
          </TabsTrigger>
          <TabsTrigger value="tasks">
            <CheckCircle className="w-4 h-4 mr-2" />
            Task Overview
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="w-4 h-4 mr-2" />
            Platform Activity
          </TabsTrigger>
        </TabsList>

        {/* User Growth Tab */}
        <TabsContent value="growth">
          <Card>
            <CardHeader>
              <CardTitle>User Growth - Last 12 Months</CardTitle>
              <CardDescription>New user signups per month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data?.user_growth?.map((month, idx) => {
                  const maxUsers = Math.max(...(data?.user_growth?.map(m => m.new_users) || [1])) || 1;
                  return (
                    <div key={idx} className="flex items-center gap-4">
                      <span className="w-20 text-sm font-medium">{month.month}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-6">
                        <div 
                          className="bg-blue-500 h-6 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Math.max((month.new_users / maxUsers) * 100, 5)}%` }}
                        >
                          <span className="text-xs text-white font-medium">{month.new_users}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform Revenue Tab */}
        <TabsContent value="revenue">
          <Card>
            <CardHeader>
              <CardTitle>Platform Revenue Trends - Last 12 Months</CardTitle>
              <CardDescription>Total invoiced vs collected across all users</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6 grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">₹{((summary.total_revenue || 0) / 100000).toFixed(2)}L</p>
                  <p className="text-sm text-gray-500">Total Invoiced</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">₹{((summary.total_collected || 0) / 100000).toFixed(2)}L</p>
                  <p className="text-sm text-gray-500">Total Collected</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">₹{((summary.total_outstanding || 0) / 100000).toFixed(2)}L</p>
                  <p className="text-sm text-gray-500">Outstanding</p>
                </div>
              </div>
              <div className="space-y-3">
                {data?.platform_revenue_trends?.map((month, idx) => {
                  const maxValue = Math.max(...(data?.platform_revenue_trends?.map(m => Math.max(m.invoiced, m.collected)) || [1])) || 1;
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium w-20">{month.month}</span>
                        <span className="text-blue-600">₹{(month.invoiced / 1000).toFixed(0)}K</span>
                        <span className="text-green-600">₹{(month.collected / 1000).toFixed(0)}K</span>
                      </div>
                      <div className="flex gap-1 h-4">
                        <div 
                          className="bg-blue-500 rounded-sm"
                          style={{ width: `${(month.invoiced / maxValue) * 50}%` }}
                        ></div>
                        <div 
                          className="bg-green-500 rounded-sm"
                          style={{ width: `${(month.collected / maxValue) * 50}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                  <span>Invoiced</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span>Collected</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Performers Tab */}
        <TabsContent value="top-users">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Top 10 Performing Users
              </CardTitle>
              <CardDescription>Ranked by revenue collected</CardDescription>
            </CardHeader>
            <CardContent>
              {data?.top_users?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>No user data available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data?.top_users?.map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between border rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                          idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-orange-400' : 'bg-blue-500'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.organization || user.email}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-8 text-right">
                        <div>
                          <p className="text-sm text-gray-500">Clients</p>
                          <p className="font-medium">{user.clients}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Invoiced</p>
                          <p className="font-medium text-blue-600">₹{(user.invoiced / 1000).toFixed(0)}K</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Collected</p>
                          <p className="font-medium text-green-600">₹{(user.collected / 1000).toFixed(0)}K</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Task Overview Tab */}
        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <CardTitle>Platform-Wide Task Statistics</CardTitle>
              <CardDescription>Aggregate task metrics across all users</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <p className="text-4xl font-bold text-blue-600">{summary.total_tasks || 0}</p>
                  <p className="text-sm text-gray-500">Total Tasks</p>
                </div>
                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <p className="text-4xl font-bold text-green-600">{summary.completed_tasks || 0}</p>
                  </div>
                  <p className="text-sm text-gray-500">Completed</p>
                </div>
                <div className="text-center p-6 bg-orange-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="w-6 h-6 text-orange-500" />
                    <p className="text-4xl font-bold text-orange-600">{summary.pending_tasks || 0}</p>
                  </div>
                  <p className="text-sm text-gray-500">Pending</p>
                </div>
                <div className="text-center p-6 bg-red-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                    <p className="text-4xl font-bold text-red-600">{summary.overdue_tasks || 0}</p>
                  </div>
                  <p className="text-sm text-gray-500">Overdue</p>
                </div>
              </div>
              
              {/* Task Completion Rate Visual */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium">Task Completion Rate</span>
                  <span className="text-sm font-bold">{averages.task_completion_rate || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-green-500 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${averages.task_completion_rate || 0}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Platform Activity Tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Platform Activity Metrics</CardTitle>
              <CardDescription>User engagement and activity across the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <Activity className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-blue-600">{data?.activity_metrics?.activity_today || 0}</p>
                  <p className="text-sm text-gray-500">Actions Today</p>
                </div>
                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <Activity className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-purple-600">{data?.activity_metrics?.activity_this_week || 0}</p>
                  <p className="text-sm text-gray-500">Actions This Week</p>
                </div>
                <div className="text-center p-6 bg-gray-100 rounded-lg">
                  <FileText className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-gray-600">{data?.activity_metrics?.total_audit_logs || 0}</p>
                  <p className="text-sm text-gray-500">Total Audit Logs</p>
                </div>
              </div>

              {/* Client Types Breakdown */}
              {data?.client_types && Object.keys(data.client_types).length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium mb-3">Clients by Category</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.client_types).map(([type, count]) => (
                      <Badge key={type} variant="outline" className="px-3 py-1">
                        <span className="capitalize">{type}</span>: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Super Admin Notice */}
      <Card className="border-purple-200 bg-purple-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Globe className="w-6 h-6 text-purple-600 mt-1" />
            <div>
              <h4 className="font-medium text-purple-900">Super Admin Exclusive</h4>
              <p className="text-sm text-purple-700 mt-1">
                This dashboard shows aggregate data across all platform users. Individual user data is isolated and 
                users can only see their own practice metrics in their Analytics Dashboard.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformAnalytics;
